import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, type DefaultValues, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { useUI } from '@/hooks/ui/useUI';
import { useUnsavedChangesWarning } from '@/hooks/ui/navigation';
import { useCatalogLoader } from '@/hooks/ui/useCatalogLoader';
import { useTranslation } from '@/i18n';
import { ROUTE_DEFINITIONS } from '@/routes';
import { scheduleService } from '@/api/domains/schedule';
import { timezoneCatalogService } from '@/api/domains/catalog/timezones';
import { shiftGroupsCatalogService } from '@/api/domains/catalog/shiftGroups';
import type { SelectorOption } from '@/components/ui/inputs/selector/Selector.types';
import {
  scheduleFormSchema,
  type ScheduleFormValues,
  type ScheduleFormSubset,
  type ScheduleWorkdayFormSubset,
  type ScheduleBreakFormSubset,
} from '@/schemas/schedule/scheduleForm.schema';
import { appendSeconds, toHHmm } from '@/utils/timeConversion';
import { injectValidationErrors } from '@/utils/formValidationErrors';

// ── Default values ─────────────────────────────────────────────────────────
const DEFAULT_VALUES: DefaultValues<ScheduleFormValues> = {
  name: '',
  isTwoDaySchedule: undefined,
  timeZoneId: undefined,
  shiftGroupId: undefined,
  startTime: '',
  entryThresholdStart: '',
  entryThresholdEnd: '',
  entryOvertimeTolerance: 0,
  entryDelayTolerance: 0,
  endTime: '',
  exitThresholdStart: '',
  exitThresholdEnd: '',
  exitOvertimeTolerance: 0,
  earlyExitTolerance: 0,
  // Colación
  hasBreakTime: false,
  breakMinutes: 0,
  breakStartTime: '',
  breakEndTime: '',
  breakEntryThresholdStart: '',
  breakEntryThresholdEnd: '',
  breakExitThresholdStart: '',
  breakExitThresholdEnd: '',
  discountBreakDelay: false,
  hasBreakMarkings: false,
};

// ── Return type ────────────────────────────────────────────────────────────
// ── Tab field mapping (in priority order) ────────────────────────────────
const TAB_FIELD_MAP = [
  { tab: 'general', fields: ['name', 'isTwoDaySchedule', 'timeZoneId', 'shiftGroupId'] },
  { tab: 'workday', fields: ['startTime', 'entryThresholdStart', 'entryThresholdEnd', 'entryOvertimeTolerance', 'entryDelayTolerance', 'endTime', 'exitThresholdStart', 'exitThresholdEnd', 'exitOvertimeTolerance', 'earlyExitTolerance'] },
  { tab: 'break', fields: ['breakStartTime', 'breakEndTime', 'breakEntryThresholdStart', 'breakEntryThresholdEnd', 'breakExitThresholdStart', 'breakExitThresholdEnd', 'breakMinutes'] },
] as const satisfies Array<{ tab: string; fields: readonly (keyof ScheduleFormValues)[] }>;

const BREAK_TIME_FIELDS = [
  'breakStartTime',
  'breakEndTime',
  'breakEntryThresholdStart',
  'breakEntryThresholdEnd',
  'breakExitThresholdStart',
  'breakExitThresholdEnd',
] as const satisfies Array<keyof ScheduleFormValues>;

export interface UseScheduleEditResult {
  formSubset: ScheduleFormSubset;
  workdayFormSubset: ScheduleWorkdayFormSubset;
  breakFormSubset: ScheduleBreakFormSubset;
  scheduleId: number | undefined;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  isSaving: boolean;
  isLoadingCatalogs: boolean;
  isDirty: boolean;
  handleSave: () => void;
  handleCancel: () => void;
  confirmNavigation: () => void;
  timezoneOptions: SelectorOption[];
  shiftGroupOptions: SelectorOption[];
  isTwoDayScheduleOptions: SelectorOption[];
}

// ── Hook ──────────────────────────────────────────────────────────────────
export function useScheduleEdit(): UseScheduleEditResult {
  const { t: translate } = useTranslation('schedule');
  const { showNotification, showApiError } = useUI();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const numericId = id !== undefined ? Number(id) : NaN;
  const scheduleId = !Number.isNaN(numericId) ? numericId : undefined;

  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [associateShift, setAssociateShift] = useState(false);
  const [activeTab, internalSetActiveTab] = useState<string>('general');
  const setActiveTab = useCallback((tab: string) => { internalSetActiveTab(tab); }, []);

  // ── Form setup ──────────────────────────────────────────────────────────
  const {
    register,
    reset,
    watch,
    setValue,
    setError,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<ScheduleFormValues>({
    resolver: zodResolver(scheduleFormSchema),
    mode: 'onBlur',
    defaultValues: DEFAULT_VALUES,
  });

  const formSubset: ScheduleFormSubset = { register, setValue, watch, errors };
  const workdayFormSubset: ScheduleWorkdayFormSubset = { register, setValue, watch, errors };

  const handleBreakTimeToggle = useCallback((checked: boolean) => {
    setValue('hasBreakTime', checked, { shouldDirty: true });
    const placeholder = '00:00';
    BREAK_TIME_FIELDS.forEach((field) => {
      setValue(field, checked ? placeholder : '', { shouldDirty: true });
    });
  }, [setValue]);

  const breakFormSubset: ScheduleBreakFormSubset = {
    register, setValue, watch, errors, onBreakTimeToggle: handleBreakTimeToggle,
  };

  // ── Load schedule data ──────────────────────────────────────────────────
  const loadSchedule = useCallback(async () => {
    if (!scheduleId) {
      setIsLoadingData(false);
      return;
    }

    setIsLoadingData(true);
    const response = await scheduleService.getById(scheduleId);

    if (!response.success || !response.data) {
      showApiError(response, translate('common:errors.loadingData'));
      setIsLoadingData(false);
      return;
    }

    const data = response.data;
    reset({
      name: data.name,
      isTwoDaySchedule: data.isTwoDaySchedule,
      timeZoneId: data.timeZoneId,
      shiftGroupId: data.shiftGroupId,
      startTime: toHHmm(data.startTime),
      entryThresholdStart: toHHmm(data.entryThresholdStart),
      entryThresholdEnd: toHHmm(data.entryThresholdEnd),
      entryOvertimeTolerance: data.entryOvertimeTolerance,
      entryDelayTolerance: data.entryDelayTolerance,
      endTime: toHHmm(data.endTime),
      exitThresholdStart: toHHmm(data.exitThresholdStart),
      exitThresholdEnd: toHHmm(data.exitThresholdEnd),
      exitOvertimeTolerance: data.exitOvertimeTolerance,
      earlyExitTolerance: data.earlyExitTolerance,
      // Colación: hasBreakTime is derived from breakMinutes (minutes mode when breakMinutes > 0)
      hasBreakTime: data.breakMinutes > 0,
      breakMinutes: data.breakMinutes,
      breakStartTime: toHHmm(data.breakStartTime ?? '00:00:00'),
      breakEndTime: toHHmm(data.breakEndTime ?? '00:00:00'),
      breakEntryThresholdStart: toHHmm(data.breakEntryThresholdStart ?? '00:00:00'),
      breakEntryThresholdEnd: toHHmm(data.breakEntryThresholdEnd ?? '00:00:00'),
      breakExitThresholdStart: toHHmm(data.breakExitThresholdStart ?? '00:00:00'),
      breakExitThresholdEnd: toHHmm(data.breakExitThresholdEnd ?? '00:00:00'),
      discountBreakDelay: data.discountBreakDelay,
      hasBreakMarkings: data.hasBreakMarkings,
    });
    setAssociateShift(data.associateShift);
    setIsLoadingData(false);
  }, [scheduleId, reset, showApiError, translate]);

  useEffect(() => {
    void loadSchedule();
  }, [loadSchedule]);

  // ── Catalogs ────────────────────────────────────────────────────────────
  const { options: timezoneOptions, isLoaded: isTimezonesLoaded } = useCatalogLoader(
    (signal) => timezoneCatalogService.getTimezones(signal),
    (item) => ({ value: String(item.id), label: item.description }),
    [],
    false,
    () => showNotification('error', translate('common:errors.loadingData')),
  );

  const { options: shiftGroupOptions, isLoaded: isShiftGroupsLoaded } = useCatalogLoader(
    (signal) => shiftGroupsCatalogService.getShiftGroups(signal),
    (item) => ({ value: String(item.id), label: item.name }),
    [],
    false,
    () => showNotification('error', translate('common:errors.loadingData')),
  );

  const isLoadingCatalogs = !isTimezonesLoaded || !isShiftGroupsLoaded || isLoadingData;

  // ── Sí/No options ────────────────────────────────────────────────────────
  const isTwoDayScheduleOptions = useMemo<SelectorOption[]>(
    () => [
      { value: 'true', label: translate('form.options.yes') },
      { value: 'false', label: translate('form.options.no') },
    ],
    [translate],
  );

  // ── Handlers ────────────────────────────────────────────────────────────
  const navigateToList = useCallback(() => {
    void navigate(ROUTE_DEFINITIONS.HORARIOS.path);
  }, [navigate]);

  const { handleCancelWithWarning: handleCancel, confirmNavigation } = useUnsavedChangesWarning({
    onConfirmLeave: navigateToList,
    isDirty,
  });

  const onSubmit = useCallback(
    async (data: ScheduleFormValues) => {
      if (!scheduleId) return;

      setIsSaving(true);

      const response = await scheduleService.update(scheduleId, {
        name: data.name,
        isTwoDaySchedule: data.isTwoDaySchedule,
        timeZoneId: data.timeZoneId,
        shiftGroupId: data.shiftGroupId,
        startTime: appendSeconds(data.startTime),
        entryThresholdStart: appendSeconds(data.entryThresholdStart),
        entryThresholdEnd: appendSeconds(data.entryThresholdEnd),
        entryOvertimeTolerance: data.entryOvertimeTolerance,
        entryDelayTolerance: data.entryDelayTolerance,
        endTime: appendSeconds(data.endTime),
        exitThresholdStart: appendSeconds(data.exitThresholdStart),
        exitThresholdEnd: appendSeconds(data.exitThresholdEnd),
        exitOvertimeTolerance: data.exitOvertimeTolerance,
        earlyExitTolerance: data.earlyExitTolerance,
        // Colación
        ...(data.hasBreakTime
          ? {
            breakMinutes: data.breakMinutes,
            breakStartTime: '00:00:00',
            breakEndTime: '00:00:00',
            breakEntryThresholdStart: '00:00:00',
            breakEntryThresholdEnd: '00:00:00',
            breakExitThresholdStart: '00:00:00',
            breakExitThresholdEnd: '00:00:00',
          }
          : {
            breakMinutes: 0,
            breakStartTime: appendSeconds(data.breakStartTime),
            breakEndTime: appendSeconds(data.breakEndTime),
            breakEntryThresholdStart: appendSeconds(data.breakEntryThresholdStart),
            breakEntryThresholdEnd: appendSeconds(data.breakEntryThresholdEnd),
            breakExitThresholdStart: appendSeconds(data.breakExitThresholdStart),
            breakExitThresholdEnd: appendSeconds(data.breakExitThresholdEnd),
          }),
        discountBreakDelay: data.discountBreakDelay,
        hasBreakMarkings: data.hasBreakMarkings,
        associateShift,
      });

      setIsSaving(false);

      if (!response.success) {
        if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
          injectValidationErrors(response.error.validationErrors, { setError });
          return;
        }
        showApiError(response);
        return;
      }

      showNotification('success', translate('messages.updateSuccess'));
      confirmNavigation();
      navigateToList();
    },
    [
      scheduleId,
      associateShift,
      confirmNavigation,
      navigateToList,
      setError,
      showApiError,
      showNotification,
      translate,
    ],
  );

  const onValidationError = useCallback((fieldErrors: FieldErrors<ScheduleFormValues>) => {
    for (const { tab, fields } of TAB_FIELD_MAP) {
      if (fields.some((field) => fieldErrors[field])) {
        internalSetActiveTab(tab);
        return;
      }
    }
  }, []);

  const handleSave = useCallback(() => {
    void handleSubmit(onSubmit, onValidationError)();
  }, [handleSubmit, onSubmit, onValidationError]);

  return {
    formSubset,
    workdayFormSubset,
    breakFormSubset,
    scheduleId,
    activeTab,
    setActiveTab,
    isSaving,
    isLoadingCatalogs,
    isDirty,
    handleSave,
    handleCancel,
    confirmNavigation,
    timezoneOptions,
    shiftGroupOptions,
    isTwoDayScheduleOptions,
  };
}
